import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CrmLayout from "../../components/CrmLayout";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Search, AlertTriangle, TrendingDown, ChevronDown, ChevronUp } from "lucide-react";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [insights, setInsights] = useState(null);
  const [showInsights, setShowInsights] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/insights/no-shows").then((r) => setInsights(r.data)).catch(() => {});
  }, []);
  const filtered = clients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.email || "").toLowerCase().includes(q.toLowerCase()));
  const totals = insights?.totals;

  return (
    <CrmLayout title="Clients">
      {insights && (totals.no_shows > 0 || totals.cancelled > 0) && (
        <div className="bg-card border border-border rounded-lg mb-5 overflow-hidden" data-testid="noshow-panel">
          <button onClick={() => setShowInsights((v) => !v)} className="w-full flex items-center justify-between px-5 py-3 border-b border-border hover:bg-secondary/40 transition-colors" data-testid="noshow-toggle">
            <div className="flex items-center gap-2"><TrendingDown size={16} className="text-accent" /><span className="font-semibold">No-show insights</span>
              <Badge className="bg-accent/20 text-accent-foreground ml-1">{totals.no_show_rate}% no-show rate</Badge></div>
            {showInsights ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showInsights && (
            <div className="p-5">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <Stat label="No-shows" value={totals.no_shows} />
                <Stat label="Cancellations" value={totals.cancelled} />
                <Stat label="Total appointments" value={totals.appointments} />
              </div>
              {!totals.deposit_enabled && totals.no_shows > 0 && (
                <div className="flex items-start gap-2 bg-accent/10 border border-accent/30 rounded-md p-3 mb-4 text-sm">
                  <AlertTriangle size={16} className="text-accent mt-0.5 shrink-0" />
                  <span>You're losing chairs to no-shows. <button className="font-medium underline" onClick={() => navigate("/settings")}>Turn on deposits</button> to secure high-risk bookings.</span>
                </div>
              )}
              {insights.clients.length > 0 && (
                <div>
                  <div className="label-eyebrow text-muted-foreground mb-2">Clients to watch</div>
                  <div className="divide-y divide-border border border-border rounded-md">
                    {insights.clients.slice(0, 6).map((c, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-2.5" data-testid={`atrisk-${i}`}>
                        <div><div className="text-sm font-medium">{c.client_name}</div><div className="text-xs text-muted-foreground">{c.client_email || c.client_phone || "—"}</div></div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-destructive">{c.no_shows} no-show{c.no_shows === 1 ? "" : "s"}</span>
                          <span className="text-muted-foreground">{c.cancelled} cancelled</span>
                          <Badge className="bg-secondary text-secondary-foreground">{c.no_show_rate}%</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search clients…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-card" data-testid="client-search" />
      </div>
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead className="text-right">Visits</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-10">No clients yet.</TableCell></TableRow>}
            {filtered.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" data-testid={`client-row-${c.id}`} onClick={() => navigate(`/clients/${c.id}`)}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{c.email || "—"}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{c.phone || "—"}</TableCell>
                <TableCell className="text-right">{c.appointment_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </CrmLayout>
  );
}

function Stat({ label, value }) {
  return <div className="bg-secondary/40 rounded-md p-3"><div className="label-eyebrow text-muted-foreground">{label}</div><div className="font-display text-2xl font-bold mt-1">{value}</div></div>;
}

